<template>
  <div class="min-h-screen bg-slate-50">
    <ProtoNavigationBar
      v-bind="$attrs"
      theme="accadisHSTheme"
      image="/img/icon-restaurant.png"
      :time="0"
    >
      <template #title>
        accadis <span class="font-bold">Infoanzeige</span>
      </template>
    </ProtoNavigationBar>

    <div class="mx-auto max-w-7xl px-6 pt-16 pb-16">
      <!-- Page header -->
      <div
        class="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-6"
      >
        <div>
          <h1 class="text-3xl font-bold text-slate-800">Screen Übersicht</h1>
          <p class="mt-1 text-slate-500">
            Alle Screens und Anzeigen der Infoanzeige auf einen Blick.
          </p>
        </div>

        <div v-if="isSignedIn" class="flex items-center gap-3">
          <span class="hidden text-sm text-slate-500 sm:inline">
            Angemeldet als
            <span class="font-medium text-slate-700">{{ signedInAs }}</span>
          </span>
          <UButton
            color="neutral"
            variant="outline"
            icon="i-mdi-logout"
            @click="auth.signOut()"
          >
            Logout
          </UButton>
        </div>
      </div>

      <!-- Signed out -->
      <div v-if="!isSignedIn" class="mx-auto mt-12 max-w-md">
        <LoginComponent />
      </div>

      <!-- Signed in -->
      <template v-else>
        <div class="mt-6 grid gap-4 lg:grid-cols-2">
          <UAlert
            color="warning"
            variant="subtle"
            icon="i-mdi-monitor-screenshot"
            title="Auflösung 1920 × 1080"
            description="Die Anzeigen sind für 1920 × 1080 optimiert. Bitte VOR dem Aufrufen einer Anzeige auf diese Auflösung wechseln – ein Nachjustieren bei bereits geöffneter Anzeige kann zu fehlerhaften Darstellungen führen."
          />
          <UAlert
            color="primary"
            variant="subtle"
            icon="i-mdi-cog-outline"
            title="Konfiguration"
            description="Screens und Anzeigen werden im Content HUB konfiguriert."
            :actions="[
              {
                label: 'Content HUB öffnen',
                trailingIcon: 'i-mdi-open-in-new',
                to: contentHubUrl,
                target: '_blank',
                color: 'primary',
                variant: 'solid',
              },
            ]"
          />
        </div>

        <UInput
          v-model="search"
          icon="i-mdi-magnify"
          placeholder="Screens und Anzeigen filtern…"
          size="lg"
          class="mt-8 w-full sm:w-96"
        />

        <div class="mt-6 grid gap-6 lg:grid-cols-2">
          <section v-for="group in groups" :key="group.title">
            <div class="mb-3 flex items-center gap-2">
              <UIcon :name="group.icon" class="size-5 text-slate-500" />
              <h2 class="text-lg font-semibold text-slate-800">
                {{ group.title }}
              </h2>
              <UBadge color="neutral" variant="soft" size="sm">
                {{ group.items.length }}
              </UBadge>
            </div>

            <div
              class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs"
            >
              <ul v-if="group.items.length" class="divide-y divide-slate-100">
                <li v-for="item in group.items" :key="item.id">
                  <NuxtLink
                    :to="`/dynamic/${item.id}`"
                    class="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <UIcon
                      :name="group.itemIcon"
                      class="size-5 shrink-0 text-slate-400 transition-colors group-hover:text-navbg"
                    />
                    <span
                      class="min-w-0 flex-1 truncate font-medium text-slate-700 group-hover:text-navbg"
                    >
                      {{ item.id }}
                    </span>
                    <UBadge
                      v-if="item.altEnabled"
                      color="warning"
                      variant="soft"
                      size="sm"
                      :title="
                        item.altConfig
                          ? `Alternative: ${item.altConfig}`
                          : undefined
                      "
                    >
                      Alt aktiv
                    </UBadge>
                    <span class="shrink-0 text-xs text-slate-400">
                      {{ item.pages.length }}
                      {{ item.pages.length === 1 ? 'Seite' : 'Seiten' }}
                    </span>
                    <UIcon
                      name="i-mdi-chevron-right"
                      class="size-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-navbg"
                    />
                  </NuxtLink>
                </li>
              </ul>
              <p v-else class="px-4 py-8 text-center text-sm text-slate-400">
                Keine Einträge gefunden.
              </p>
            </div>
          </section>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
const configUrl = '/api/v1/info-screen-config';
const contentHubUrl =
  'https://contenthub.accadis-bildung.de/admin/content/Infoanzeige';
const { data, error } = useFetch(configUrl);
if (error.value) throw createError(error.value);

const auth = useAuth();
const isSignedIn = computed(() => auth.status.value === 'authenticated');
const signedInAs = computed(() => auth.data.value?.user?.email ?? 'unbekannt');

const search = ref('');

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  const items = data.value?.data ?? [];
  return term
    ? items.filter((item) => item.id.toLowerCase().includes(term))
    : items;
});

const groups = computed(() => [
  {
    title: 'Screens',
    icon: 'i-mdi-monitor-multiple',
    itemIcon: 'i-mdi-monitor',
    items: filtered.value.filter((item) => item.status != 'archived'),
  },
  {
    title: 'Anzeigen',
    icon: 'i-mdi-view-dashboard-outline',
    itemIcon: 'i-mdi-television-play',
    items: filtered.value.filter((item) => item.status === 'archived'),
  },
]);

useHead({
  meta: [{ hid: 'robots', name: 'robots', content: 'noindex, nofollow' }],
});
</script>
